import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Calendar, RotateCcw, X, MessageSquare, Zap, Edit, Trash, MoreVertical } from 'lucide-react';
import {
  getConsumptionsByBuildingId,
  deleteConsumption,
  updateConsumption,
  formatAmount
} from '../../Fetch/fetchElectricVehicleConsumption';
import { sendWhatsAppMessage } from '../../utils/whatsappUtils';
import styles from './ElectricVehicleList.module.css';

// Action Menu Modal Component
const ActionMenu = ({ consumption, onEdit, onDelete, onWhatsAppShare, loading }) => {
  const [isOpen, setIsOpen] = useState(false);

  const actions = [
    {
      label: '📱 שלח WhatsApp',
      onClick: () => onWhatsAppShare(consumption),
      disabled: loading
    },
    {
      label: '✏️ ערוך',
      onClick: () => onEdit(consumption),
      disabled: loading
    },
    {
      label: '🗑️ מחק',
      onClick: () => onDelete(consumption.id),
      disabled: loading,
      isDanger: true
    }
  ];

  const handleActionClick = (action) => {
    if (!action.disabled) {
      action.onClick();
      setIsOpen(false);
    }
  };

  return (
    <>
      <button
        className={`${styles.actionMenuTrigger} ${loading ? styles.disabled : ''}`}
        onClick={() => setIsOpen(true)}
        disabled={loading}
        aria-label="פתח תפריט פעולות"
      >
        <span>⋯</span>
      </button>

      {isOpen && createPortal(
        <div className={styles.modalOverlay} onClick={() => setIsOpen(false)}>
          <div className={styles.actionMenuModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.actionMenuHeader}>
              <h3>פעולות</h3>
              <button
                className={styles.closeButton}
                onClick={() => setIsOpen(false)}
                aria-label="סגור"
              >
                ✕
              </button>
            </div>
            <div className={styles.actionMenuContent}>
              {actions.map((action, index) => (
                <button
                  key={index}
                  className={`${styles.actionMenuItem} ${action.isDanger ? styles.dangerAction : ''} ${action.disabled ? styles.disabled : ''}`}
                  onClick={() => handleActionClick(action)}
                  disabled={action.disabled}
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

// Mobile Consumption Card Component
const MobileConsumptionCard = ({ consumption, onEdit, onDelete, onWhatsAppShare }) => {
  const [showMenu, setShowMenu] = useState(false);
  const balance = Math.abs(consumption.totalMoneyNeedPay - consumption.totalMoneyPaid);

  const handleEdit = () => {
    setShowMenu(false);
    onEdit(consumption);
  };

  const handleDelete = () => {
    setShowMenu(false);
    onDelete(consumption.id);
  };

  const handleWhatsApp = () => {
    setShowMenu(false);
    onWhatsAppShare(consumption);
  };

  return (
    <div className={styles.mobileConsumptionCard}>
      {/* Menu Button - Top Right */}
      <div className={styles.menuContainer}>
        <button className={styles.menuBtn} onClick={() => setShowMenu(!showMenu)}>
          <MoreVertical size={20} />
        </button>
        {showMenu && (
          <>
            <div className={styles.menuOverlay} onClick={() => setShowMenu(false)} />
            <div className={styles.menuDropdown}>
              <button className={styles.menuItem} onClick={handleEdit}>
                <Edit size={16} />
                <span>ערוך</span>
              </button>
              <button className={styles.menuItem} onClick={handleWhatsApp}>
                <MessageSquare size={16} />
                <span>שלח WhatsApp</span>
              </button>
              <button className={`${styles.menuItem} ${styles.menuItemDanger}`} onClick={handleDelete}>
                <Trash size={16} />
                <span>מחק</span>
              </button>
            </div>
          </>
        )}
      </div>

      {/* Avatar - Top Left with Zap icon */}
      <div className={styles.consumptionAvatar}>
        <Zap size={36} className={styles.avatarIcon} />
      </div>

      {/* Consumption Info */}
      <div className={styles.consumptionInfo}>
        <h2 className={styles.consumptionName}>חניה {consumption.parkingNumber}</h2>
        <p className={styles.consumptionApartment}>דירה {consumption.apartmentNumber}</p>
        <p className={styles.consumptionUser}>{consumption.userName}</p>
      </div>

      {/* Stats */}
      <div className={styles.consumptionStats}>
        <div className={styles.statItem}>
          <div className={styles.statLabel}>צריכה</div>
          <div className={styles.statValue}>{consumption.totalPowerConsumptionPerMonth.toFixed(2)} kWh</div>
        </div>
        <div className={styles.statDivider}></div>
        <div className={styles.statItem}>
          <div className={styles.statLabel}>לתשלום</div>
          <div className={styles.statValue}>{formatAmount(consumption.totalMoneyNeedPay)}</div>
        </div>
        <div className={styles.statDivider}></div>
        <div className={styles.statItem}>
          <div className={styles.statLabel}>יתרה</div>
          <div className={`${styles.statValue} ${balance > 0 ? styles.unpaid : styles.paid}`}>
            {formatAmount(balance)}
          </div>
        </div>
      </div>

      {/* Additional Details */}
      <div className={styles.consumptionDetails}>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>תאריך:</span>
          <span className={styles.detailValue}>{new Date(consumption.date).toLocaleDateString('he-IL')}</span>
        </div>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>טלפון:</span>
          <span className={styles.detailValue}>{consumption.phoneNumber}</span>
        </div>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>מונה:</span>
          <span className={styles.detailValue}>{consumption.counter.toFixed(2)}</span>
        </div>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>מחיר לקילוואט:</span>
          <span className={styles.detailValue}>₪{consumption.powerConsumption.toFixed(2)}</span>
        </div>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>שולם:</span>
          <span className={styles.detailValue}>{formatAmount(consumption.totalMoneyPaid)}</span>
        </div>
      </div>
    </div>
  );
};

const ElectricVehicleList = () => {
  const navigate = useNavigate();
  const [consumptions, setConsumptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [showOnlyUnpaid, setShowOnlyUnpaid] = useState(false);
  const [buildingId, setBuildingId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);

  useEffect(() => {
    // Get buildingId from localStorage
    const user = JSON.parse(localStorage.getItem('user'));
    if (user && user.buildingId) {
      setBuildingId(user.buildingId);
    }
  }, []);

  useEffect(() => {
    if (buildingId) {
      fetchConsumptions();
    }
  }, [buildingId]);

  const fetchConsumptions = async () => {
    if (!buildingId) {
      setError('לא נמצא מזהה בניין');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await getConsumptionsByBuildingId(buildingId);
      setConsumptions(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק רשומה זו?')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await deleteConsumption(id);
      await fetchConsumptions();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterMonth('');
    setFilterYear(new Date().getFullYear());
    setShowOnlyUnpaid(false);
  };

  const handleWhatsAppShare = (consumption) => {
    try {
      const balance = Math.abs(consumption.totalMoneyNeedPay - consumption.totalMoneyPaid);
      const balanceType = (consumption.totalMoneyNeedPay - consumption.totalMoneyPaid) > 0 ? 'חוב' : 'זכות';

      let message = `🔌 *קבלה על צריכת חשמל*\n`;
      message += `${'─'.repeat(30)}\n\n`;
      message += `📅 *תאריך:* ${new Date(consumption.date).toLocaleDateString('he-IL')}\n`;
      message += `👤 *שם:* ${consumption.userName}\n`;
      message += `🏠 *דירה:* ${consumption.apartmentNumber}\n`;
      message += `🚗 *חניה:* ${consumption.parkingNumber}\n`;
      message += `📞 *טלפון:* ${consumption.phoneNumber}\n\n`;

      message += `⚡ *פרטי צריכה:*\n`;
      message += `• מונה: ${consumption.counter.toFixed(2)}\n`;
      message += `• מחיר לקילוואט: ₪${consumption.powerConsumption.toFixed(2)}\n`;
      message += `• צריכה חודשית: ${consumption.totalPowerConsumptionPerMonth.toFixed(2)} kWh\n\n`;

      message += `💰 *פירוט כספי:*\n`;
      message += `• לתשלום: ${formatAmount(consumption.totalMoneyNeedPay)}\n`;
      message += `• שולם: ${formatAmount(consumption.totalMoneyPaid)}\n`;
      message += `• יתרה (${balanceType}): ${formatAmount(balance)}\n\n`;

      message += `${'─'.repeat(30)}\n`;
      message += `💻 מערכת ניהול ועד הבית\n`;
      message += `⏰ ${new Date().toLocaleString('he-IL')}`;

      const phoneNumber = consumption.phoneNumber;
      const whatsappResult = sendWhatsAppMessage(message, phoneNumber);

      if (whatsappResult) {
        setTimeout(() => {
          alert(`✅ הודעת WhatsApp נוצרה בהצלחה!\n\n📱 WhatsApp נפתח לשליחה ל${consumption.userName} (${phoneNumber})`);
        }, 500);
      } else {
        alert('⚠️ לא ניתן לפתוח WhatsApp. בדוק הגדרות הדפדפן.');
      }
    } catch (error) {
      console.error('❌ WhatsApp Share Error:', error);
      alert(`❌ שגיאה ביצירת הודעת WhatsApp: ${error.message}`);
    }
  };


  const filteredConsumptions = consumptions.filter(consumption => {
    const matchSearch =
      consumption.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      consumption.phoneNumber.includes(searchTerm) ||
      consumption.parkingNumber.toString().includes(searchTerm);

    // חישוב יתרה
    const balance = Math.abs(consumption.totalMoneyNeedPay - consumption.totalMoneyPaid);

    // סינון לפי יתרה
    const matchUnpaid = !showOnlyUnpaid || balance !== 0;

    if (!filterMonth) return matchSearch && matchUnpaid;

    const consumptionDate = new Date(consumption.date);
    const matchMonth = consumptionDate.getMonth() + 1 === parseInt(filterMonth);
    const matchYear = consumptionDate.getFullYear() === parseInt(filterYear);

    return matchSearch && matchMonth && matchYear && matchUnpaid;
  });

  const totalConsumption = filteredConsumptions.reduce((sum, c) => sum + c.totalPowerConsumptionPerMonth, 0);
  const totalAmount = filteredConsumptions.reduce((sum, c) => sum + c.totalMoneyNeedPay, 0);
  const totalPaid = filteredConsumptions.reduce((sum, c) => sum + c.totalMoneyPaid, 0);
  const totalUnpaid = totalAmount - totalPaid;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1>ניהול צריכת חשמל</h1>
          {(filterMonth || filterYear) && (
            <div className={styles.periodDisplay}>
              <Calendar size={16} />
              <span>
                {filterMonth ? `${filterMonth}/` : ''}{filterYear}
              </span>
            </div>
          )}
        </div>
        <div className={styles.headerActions}>
          <button
            className={styles.filterButton}
            onClick={() => setShowFilters(true)}
            title="סנן"
          >
            <Filter size={18} />
            סנן
            {(searchTerm || filterMonth || filterYear !== new Date().getFullYear() || showOnlyUnpaid) && (
              <span className={styles.activeFilterBadge}></span>
            )}
          </button>
          <button
            className={styles.addButton}
            onClick={() => navigate('/app/electric-vehicle/create')}
          >
            + הוסף צריכה
          </button>
        </div>
      </div>

      {error && (
        <div className={styles.error}>
          <p>{error}</p>
        </div>
      )}

      {showFilters && (
        <div className={styles.modalOverlay} onClick={() => setShowFilters(false)}>
          <div className={`${styles.modalContent} ${styles.filtersModal}`} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>
                <Filter size={20} />
                סינון צריכה
              </h3>
              <button
                onClick={() => setShowFilters(false)}
                className={styles.userModalCloseButton}
                aria-label="סגור"
              >
                <X size={20} />
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label>חיפוש</label>
                <div className={styles.searchBar}>
                  <input
                    type="text"
                    placeholder="חיפוש צריכה..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Search size={20} className={styles.searchIcon} />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>חודש</label>
                <div className={styles.dateFilter}>
                  <select
                    value={filterMonth}
                    onChange={(e) => setFilterMonth(e.target.value)}
                    className={styles.dateSelect}
                  >
                    <option value="">כל החודשים</option>
                    <option value="1">ינואר</option>
                    <option value="2">פברואר</option>
                    <option value="3">מרץ</option>
                    <option value="4">אפריל</option>
                    <option value="5">מאי</option>
                    <option value="6">יוני</option>
                    <option value="7">יולי</option>
                    <option value="8">אוגוסט</option>
                    <option value="9">ספטמבר</option>
                    <option value="10">אוקטובר</option>
                    <option value="11">נובמבר</option>
                    <option value="12">דצמבר</option>
                  </select>
                  <Calendar size={18} className={styles.filterIcon} />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>שנה</label>
                <div className={styles.dateFilter}>
                  <select
                    value={filterYear}
                    onChange={(e) => setFilterYear(e.target.value)}
                    className={styles.dateSelect}
                  >
                    {[...Array(5)].map((_, i) => {
                      const year = new Date().getFullYear() - i;
                      return <option key={year} value={year}>{year}</option>;
                    })}
                  </select>
                  <Calendar size={18} className={styles.filterIcon} />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={showOnlyUnpaid}
                    onChange={(e) => setShowOnlyUnpaid(e.target.checked)}
                  />
                  הצג חייבים בלבד
                </label>
              </div>
            </div>

            <div className={styles.modalActions}>
              {(searchTerm || filterMonth || filterYear !== new Date().getFullYear() || showOnlyUnpaid) && (
                <button
                  onClick={clearFilters}
                  className={styles.resetButton}
                  title="נקה מסננים"
                >
                  <RotateCcw size={16} />
                  נקה הכל
                </button>
              )}
              <button
                onClick={() => setShowFilters(false)}
                className={styles.confirmButton}
              >
                <Filter size={16} />
                החל
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.summary}>
        <div className={styles.summaryCard}>
          <h3>סה"כ צריכה</h3>
          <p>{totalConsumption.toFixed(2)} kWh</p>
        </div>
        <div className={styles.summaryCard}>
          <h3>סה"כ לתשלום</h3>
          <p>{formatAmount(totalAmount)}</p>
        </div>
        <div className={styles.summaryCard}>
          <h3>סה"כ שולם</h3>
          <p className={styles.paid}>{formatAmount(totalPaid)}</p>
        </div>
        <div className={styles.summaryCard}>
          <h3>יתרת חוב לדייר</h3>
          <p className={styles.unpaid}>{formatAmount(totalUnpaid)}</p>
        </div>
      </div>

      <div className={styles.tableContainer}>
        {loading ? (
          <div className={styles.loading}>טוען נתונים...</div>
        ) : filteredConsumptions.length === 0 ? (
          <div className={styles.noData}>אין רשומות להצגה</div>
        ) : (
          <>
            {/* Desktop Table View */}
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>תאריך</th>
                  <th>חניה</th>
                  <th>דירה</th>
                  <th>שם</th>
                  <th>טלפון</th>
                  <th>מונה</th>
                  <th>מחיר לקילוואט</th>
                  <th>צריכה (kWh)</th>
                  <th>לתשלום</th>
                  <th>שולם</th>
                  <th>יתרה</th>
                  <th>פעולות</th>
                </tr>
              </thead>
              <tbody>
                {filteredConsumptions.map((consumption) => {
                  const balance = Math.abs(consumption.totalMoneyNeedPay - consumption.totalMoneyPaid);
                  return (
                    <tr key={consumption.id}>
                      <td>{new Date(consumption.date).toLocaleDateString('he-IL')}</td>
                      <td>{consumption.parkingNumber}</td>
                      <td>{consumption.apartmentNumber}</td>
                      <td>{consumption.userName}</td>
                      <td>{consumption.phoneNumber}</td>
                      <td>{consumption.counter.toFixed(2)}</td>
                      <td>₪{consumption.powerConsumption.toFixed(2)}</td>
                      <td>{consumption.totalPowerConsumptionPerMonth.toFixed(2)}</td>
                      <td>{formatAmount(consumption.totalMoneyNeedPay)}</td>
                      <td className={styles.paid}>{formatAmount(consumption.totalMoneyPaid)}</td>
                      <td className={balance > 0 ? styles.unpaid : styles.paid}>
                        {formatAmount(balance)}
                      </td>
                      <td>
                        <ActionMenu
                          consumption={consumption}
                          onEdit={(cons) => navigate(`/app/electric-vehicle/edit/${cons.id}`)}
                          onDelete={handleDelete}
                          onWhatsAppShare={handleWhatsAppShare}
                          loading={loading}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Mobile Card View */}
            <div className={styles.mobileCardsContainer}>
              {filteredConsumptions.map((consumption) => (
                <MobileConsumptionCard
                  key={consumption.id}
                  consumption={consumption}
                  onEdit={(cons) => navigate(`/app/electric-vehicle/edit/${cons.id}`)}
                  onDelete={handleDelete}
                  onWhatsAppShare={handleWhatsAppShare}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Mobile Search Button - Rendered outside main container */}
      {!showMobileSearch && createPortal(
        <button
          className={styles.mobileSearchButton}
          onClick={() => setShowMobileSearch(true)}
          aria-label="פתח חיפוש"
        >
          <Search size={22} />
          {(searchTerm || filterMonth || filterYear !== new Date().getFullYear() || showOnlyUnpaid) && (
            <span className={styles.searchIndicatorDot}></span>
          )}
        </button>,
        document.body
      )}

      {/* Mobile Search Modal */}
      {showMobileSearch && createPortal(
        <div className={styles.mobileSearchModal}>
          <div className={styles.mobileSearchHeader}>
            <h2>חיפוש וסינון</h2>
            <button
              className={styles.mobileSearchClose}
              onClick={() => setShowMobileSearch(false)}
              aria-label="סגור"
            >
              <X size={24} />
            </button>
          </div>

          <div className={styles.mobileSearchContent}>
            {/* Search Input */}
            <div className={styles.mobileSearchField}>
              <Search size={20} className={styles.mobileSearchIcon} />
              <input
                type="text"
                placeholder="חיפוש צריכה..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.mobileSearchInput}
              />
            </div>

            {/* Month Filter */}
            <div className={styles.mobileSearchField}>
              <Calendar size={20} className={styles.mobileSearchIcon} />
              <select
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
                className={styles.mobileSearchSelect}
              >
                <option value="">כל החודשים</option>
                <option value="1">ינואר</option>
                <option value="2">פברואר</option>
                <option value="3">מרץ</option>
                <option value="4">אפריל</option>
                <option value="5">מאי</option>
                <option value="6">יוני</option>
                <option value="7">יולי</option>
                <option value="8">אוגוסט</option>
                <option value="9">ספטמבר</option>
                <option value="10">אוקטובר</option>
                <option value="11">נובמבר</option>
                <option value="12">דצמבר</option>
              </select>
            </div>

            {/* Year Filter */}
            <div className={styles.mobileSearchField}>
              <Calendar size={20} className={styles.mobileSearchIcon} />
              <select
                value={filterYear}
                onChange={(e) => setFilterYear(Number(e.target.value))}
                className={styles.mobileSearchSelect}
              >
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            {/* Show Only Unpaid Checkbox */}
            <div className={styles.mobileSearchCheckbox}>
              <label>
                <input
                  type="checkbox"
                  checked={showOnlyUnpaid}
                  onChange={(e) => setShowOnlyUnpaid(e.target.checked)}
                />
                <span>הצג רק לא שולם</span>
              </label>
            </div>

            {/* Actions */}
            <div className={styles.mobileSearchActions}>
              {(searchTerm || filterMonth || filterYear !== new Date().getFullYear() || showOnlyUnpaid) && (
                <button
                  className={styles.mobileSearchClear}
                  onClick={() => {
                    setSearchTerm('');
                    setFilterMonth('');
                    setFilterYear(new Date().getFullYear());
                    setShowOnlyUnpaid(false);
                  }}
                >
                  <RotateCcw size={16} />
                  נקה הכל
                </button>
              )}
              <button
                className={styles.mobileSearchApply}
                onClick={() => setShowMobileSearch(false)}
              >
                <Filter size={16} />
                החל
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ElectricVehicleList;
